import {PayRunModel} from "../../redux/types";
import {createUseStyles} from "react-jss";
import {Colors} from "../../constants/colors";
import {useState} from "react";
import PayRunModal from "./PayRunModal";
import InputField from "../InputField";
import Pagination from "../Pagination";
import {PayRunPage} from "../../redux/slices/payRunDashboardSlice";
import {Alert} from "react-bootstrap";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';

interface Props {
  payRunPage: PayRunPage
  onFilterChange: (value: string) => void
  onPageChange: (pageIndex: number) => void
  columns: ColumnDef<PayRunModel>[]
}

const PayRunList = ({payRunPage, onFilterChange, onPageChange, columns}: Props) => {
  const [selectedPayRun, setSelectedPayRun] = useState<PayRunModel>()
  const styles = useStyles()

  const table = useReactTable({
    data: payRunPage.payRuns,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(payRunPage.totalCount / payRunPage.pageSize),
  });

  return (
    <>
      <div className={styles.container}>
        <InputField
          id="userSearchField"
          placeholder="Filter pay runs..."
          value={payRunPage.searchTerm}
          onEnterKeyOrBlur={value => onFilterChange(value)}
        />
        {
          payRunPage.error &&
            <Alert variant="danger">
              {payRunPage.error instanceof Error ? payRunPage.error.message : payRunPage.error}
            </Alert>
        }

        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id} className={styles.headerRow}>
                  {headerGroup.headers.map(header => (
                    <th key={header.id} className={styles.headerCell}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  className={styles.bodyRow}
                  onClick={() => setSelectedPayRun(row.original)}
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className={styles.bodyCell}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.pagination}>
          <Pagination
            pageIndex={payRunPage.pageIndex}
            itemCount={payRunPage.totalCount}
            pageSize={payRunPage.pageSize}
            siblingCount={1}
            onPageChange={onPageChange}
          />
        </div>
      </div>
      <PayRunModal
        payRun={selectedPayRun}
        visible={selectedPayRun !== undefined}
        onCloseClick={() => setSelectedPayRun(undefined)}/>
    </>
  )
}

export default PayRunList

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  tableContainer: {
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 0px',
    fontSize: '14px',
  },
  headerRow: {
    backgroundColor: 'transparent',
  },
  headerCell: {
    padding: '6px 12px',
    textAlign: 'left',
    backgroundColor: Colors.darkGrayCard,
    borderRadius: '4px',
    fontWeight: 'bold',
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  bodyRow: {
    backgroundColor: Colors.darkGrayCard,
    cursor: 'pointer',
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    },
  },
  bodyCell: {
    padding: '2px 12px',
    textAlign: 'left',
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center'
  }
})
